import {
  QuotationFactor,
  useBulkProductPremiumProjections,
} from '@bowtie-web/common';
import { omit } from 'lodash';
import { useMemo } from 'react';
import { getTotalPremiumFromProjections } from '../helpers';

type PremiumComparisonFactor = Omit<QuotationFactor, 'product_id'> & {
  projection_years: number;
};

/**
 * Compares premium projections between two products (same type apple-to-apple comparison)
 * @param productCodeA - Product code for first product
 * @param productCodeB - Product code for second product
 * @param premiumComparisonFactor - Common factor for the quotation
 * @param skip - Whether to skip the API call for premium projections
 */
const useTotalPremiumComparison = (
  productCodeA: string,
  productCodeB: string,
  premiumComparisonFactor: PremiumComparisonFactor,
  skip?: boolean,
) => {
  const rephrasedFactor = useMemo(
    () => ({
      ...omit(premiumComparisonFactor, 'sum_assured'),
      // Apply the same sum assured to both products for an apples-to-apples comparison
      products: [productCodeA, productCodeB].map(productCode => ({
        code: productCode,
        sum_assured: premiumComparisonFactor.sum_assured,
      })),
    }),
    [productCodeA, productCodeB, premiumComparisonFactor],
  );

  const [projections] = useBulkProductPremiumProjections(rephrasedFactor, {
    skip: skip || !productCodeA || !productCodeB,
  });

  const totalPremiums = useMemo(() => {
    if (!projections.length) {
      return null;
    }

    const totalPremiumA = getTotalPremiumFromProjections(
      projections,
      productCodeA,
    );

    const totalPremiumB = getTotalPremiumFromProjections(
      projections,
      productCodeB,
    );

    if (totalPremiumA === undefined || totalPremiumB === undefined) {
      return null;
    }

    return {
      [productCodeA]: totalPremiumA,
      [productCodeB]: totalPremiumB,
    };
  }, [projections, productCodeA, productCodeB]);

  return useMemo(() => {
    if (!totalPremiums) {
      return {
        totalPremiums: null,
        premiumDifference: null,
        percentageDifference: null,
      };
    }

    const premiumA = totalPremiums[productCodeA];
    const premiumB = totalPremiums[productCodeB];
    const premiumDifference = Math.abs(premiumA - premiumB);
    const percentageDifference = premiumDifference
      ? Math.round((premiumDifference / Math.max(premiumA, premiumB)) * 100)
      : 0;

    return { totalPremiums, premiumDifference, percentageDifference };
  }, [productCodeA, productCodeB, totalPremiums]);
};

export default useTotalPremiumComparison;
